import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Location Component
 * Manages camera transitions and content display for specific locations
 */
export function Location({ 
  locationData, 
  onEnter, 
  onExit, 
  isActive 
}) {
  const { camera } = useThree()
  const transitionRef = useRef({
    startPosition: new THREE.Vector3(),
    startTarget: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
    endTarget: new THREE.Vector3(),
    progress: 0,
    duration: 2.0,
    isTransitioning: false
  })

  useEffect(() => {
    if (!isActive || !locationData) return

    // Start camera transition
    const transition = transitionRef.current
    const { position, target, transitionDuration } = locationData.camera

    // Store current camera state
    transition.startPosition.copy(camera.position)
    transition.startTarget.set(0, 0, 0) // Assuming looking at origin

    // Set target camera state
    transition.endPosition.set(...position)
    transition.endTarget.set(...target)
    transition.duration = transitionDuration || 2.0
    transition.progress = 0
    transition.isTransitioning = true

    // Trigger onEnter callback
    if (onEnter) {
      onEnter(locationData)
    }

    // Animate camera transition
    const startTime = Date.now()
    let rafId = null
    
    const animate = () => {
      if (!transition.isTransitioning) return

      const elapsed = (Date.now() - startTime) / 1000
      transition.progress = Math.min(elapsed / transition.duration, 1)

      // Ease in-out cubic
      const t = transition.progress < 0.5
        ? 4 * transition.progress ** 3
        : 1 - Math.pow(-2 * transition.progress + 2, 3) / 2

      // Interpolate camera position
      camera.position.lerpVectors(
        transition.startPosition,
        transition.endPosition,
        t
      )

      // Update camera FOV if specified
      if (locationData.camera.fov) {
        camera.fov = THREE.MathUtils.lerp(
          camera.fov,
          locationData.camera.fov,
          t * 0.1
        )
        camera.updateProjectionMatrix()
      }

      if (transition.progress < 1) {
        rafId = requestAnimationFrame(animate)
      } else {
        transition.isTransitioning = false
      }
    }

    animate()

    // Cleanup on exit
    return () => {
      // Cancel animation frame to prevent memory leak
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
      // Stop transition
      transition.isTransitioning = false
      
      if (onExit) {
        onExit(locationData)
      }
    }
  }, [isActive, locationData, camera, onEnter, onExit])

  return null // This component doesn't render anything in the 3D scene
}